import './index.css';
import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, memo, useRef, useCallback, useState, useLayoutEffect } from 'react';
import classnames from 'classnames';
import '@hh.ru/magritte-common-focus-visible';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { ComponentWithBadge, Badge } from '@hh.ru/magritte-ui-badge';
import { useScrollContext } from '@hh.ru/magritte-ui-scrollable-container';
import { TooltipHover } from '@hh.ru/magritte-ui-tooltip';
import { Text } from '@hh.ru/magritte-ui-typography';

var styles = {"tabs":"magritte-tabs___VcV8C_6-0-8","tab-wrapper":"magritte-tab-wrapper___9j9qk_6-0-8","tabWrapper":"magritte-tab-wrapper___9j9qk_6-0-8","tab":"magritte-tab___B0BeU_6-0-8","tab_selected":"magritte-tab_selected___w9PT2_6-0-8","tabSelected":"magritte-tab_selected___w9PT2_6-0-8","tab-wrapper_primary":"magritte-tab-wrapper_primary___yt3sT_6-0-8","tabWrapperPrimary":"magritte-tab-wrapper_primary___yt3sT_6-0-8","tab_primary":"magritte-tab_primary___3Of2H_6-0-8","tabPrimary":"magritte-tab_primary___3Of2H_6-0-8","tab-label":"magritte-tab-label___nFmge_6-0-8","tabLabel":"magritte-tab-label___nFmge_6-0-8","postfix":"magritte-postfix___30ZxD_6-0-8","tab-content":"magritte-tab-content___onvTJ_6-0-8","tabContent":"magritte-tab-content___onvTJ_6-0-8","tab-wrapper_secondary":"magritte-tab-wrapper_secondary___avLln_6-0-8","tabWrapperSecondary":"magritte-tab-wrapper_secondary___avLln_6-0-8","tab_secondary":"magritte-tab_secondary___-00LL_6-0-8","tabSecondary":"magritte-tab_secondary___-00LL_6-0-8","postfix-wrapper":"magritte-postfix-wrapper___1dORq_6-0-8","postfixWrapper":"magritte-postfix-wrapper___1dORq_6-0-8","indicator-wrapper":"magritte-indicator-wrapper___Q68i7_6-0-8","indicatorWrapper":"magritte-indicator-wrapper___Q68i7_6-0-8","indicator":"magritte-indicator___irp4W_6-0-8","animate":"magritte-animate___5GxTP_6-0-8"};

const TabForwardRefRenderFunc = ({ Element = 'button', badge = false, children, 'data-qa': dataQa = 'tab', focused, hidden, id, itemRef, mode = 'primary', onClick, onSelect, postfix, selected, ...buttonProps }, ref) => {
    const activatorRef = useRef(null);
    const elementRef = useRef(null);
    const labelRef = useRef(null);
    const mouseEventRef = useRef(false);
    const { removeItem, registerItem, scrollToItem } = useScrollContext();
    const scrollItemCallback = useCallback((element) => {
        if (element !== null) {
            elementRef.current = element;
            registerItem(elementRef);
        }
        else {
            removeItem(elementRef);
            elementRef.current = null;
        }
    }, [registerItem, removeItem]);
    const elementRefCallback = useMultipleRefs(scrollItemCallback, itemRef, ref);
    const [showTooltip, setShowTooltip] = useState(false);
    useLayoutEffect(() => {
        if (focused && elementRef.current) {
            elementRef.current.focus();
        }
    }, [focused]);
    useLayoutEffect(() => {
        if (selected && elementRef.current) {
            scrollToItem(elementRef);
            if (showTooltip && labelRef.current) {
                labelRef.current.dispatchEvent(new MouseEvent('mouseout', { bubbles: true, cancelable: true }));
            }
        }
    }, [selected, scrollToItem, showTooltip]);
    useLayoutEffect(() => {
        if (mode === 'secondary' &&
            labelRef.current !== null &&
            labelRef.current.scrollWidth > labelRef.current.offsetWidth) {
            setShowTooltip(true);
        }
    }, [mode, setShowTooltip]);
    const handleFocus = useCallback(() => {
        if (!mouseEventRef.current) {
            scrollToItem(elementRef);
        }
    }, [scrollToItem]);
    const setMouseEventRef = useCallback(() => (mouseEventRef.current = true), []);
    const resetMouseEventRef = useCallback(() => (mouseEventRef.current = false), []);
    const handleWheel = useCallback(() => {
        if (labelRef?.current) {
            labelRef.current.dispatchEvent(new MouseEvent('mouseout', { bubbles: true, cancelable: true }));
        }
    }, []);
    if (hidden) {
        return null;
    }
    const handleClick = (event) => {
        onSelect?.(id);
        onClick?.(event);
    };
    const content = (jsxs("span", { className: styles.tabContent, ref: activatorRef, children: [jsx("span", { className: styles.tabLabel, ref: labelRef, children: jsx(Text, { Element: "span", typography: mode === 'primary' ? 'subtitle-1-semibold' : 'label-3-regular', children: children }) }), postfix && (jsx("span", { className: styles.postfixWrapper, "data-qa": "tab-postfix", children: jsx(Text, { Element: "span", typography: mode === 'primary' ? 'subtitle-1-semibold' : 'subtitle-2-semibold', children: jsx("span", { className: styles.postfix, children: postfix }) }) }))] }));
    const tabElement = (jsxs(Element, { ...buttonProps, type: "button", ref: elementRefCallback, role: "tab", "data-qa": dataQa, onClick: handleClick, className: classnames(styles.tab, { [styles.tabSelected]: selected, [styles[`tab_${mode}`]]: !!mode }), "aria-selected": selected, onFocus: handleFocus, onMouseDown: setMouseEventRef, onMouseLeave: resetMouseEventRef, onMouseUp: resetMouseEventRef, onWheel: showTooltip ? handleWheel : undefined, children: [content, showTooltip && (jsx(TooltipHover, { activatorRef: activatorRef, placement: "top-center", children: children }))] }));
    return (jsx("div", { className: classnames(styles.tabWrapper, { [styles[`tab-wrapper_${mode}`]]: !!mode }), children: badge && mode === 'primary' ? (jsx(ComponentWithBadge, { badge: jsx(Badge, { size: "extra-small" }), offset: 2, children: tabElement })) : (tabElement) }));
};
const TabComponent = forwardRef(TabForwardRefRenderFunc);
const Tab = memo(TabComponent);

export { Tab as T, styles as s };
//# sourceMappingURL=Tab-DqAw76GN.js.map
